/**
 * @format
 */
import { StylingStatus } from "../types/editor-types";

export class BaseToolService {
   protected isP = (node: Node) => this.isNodeNameEqual(node, "P");
   protected isB = (node: Node) => this.isNodeNameEqual(node, "B") || this.isNodeNameEqual(node, "STRONG");
   protected isU = (node: Node) => this.isNodeNameEqual(node, "U");
   protected isBR = (node: Node) => this.isNodeNameEqual(node, "BR");
   protected isSPAN = (node: Node) => this.isNodeNameEqual(node, "SPAN");
   protected isA = (node: Node) => this.isNodeNameEqual(node, "A");
   protected isText = (node: Node) => this.isNodeNameEqual(node, "#text");
   protected isDiv = (node: Node) => this.isNodeNameEqual(node, "DIV");
   protected isInput = (node: Node) => this.isNodeNameEqual(node, "INPUT");
   protected isTemplateContext = (node: Node) => this.isNodeNameEqual(node, "TEMPLATE-CONTEXT");

   protected isNodeNameEqual = (node: Node, nodeName: string) => {
      return Boolean(node) && node.nodeName === nodeName;
   };

   protected isTextEmptyValue(node:Node): boolean {
     return this.isText(node) && node.nodeValue === ""
  }

   protected isLastBr(node: Node): boolean {
      return this.isBR(node) && !this.isBR(node.nextSibling);
   }

   protected isRootElementChild(node: Element, rootElementId: string): boolean {
      return this.hasParentIdEqualTo(node, rootElementId) || this.hasParentNgModelAttribute(node);
   }

   protected hasStyledParent(node: Node): boolean {
      return (
         Boolean(node.parentNode) &&
         (this.isB(node.parentNode) ||
            this.isU(node.parentNode) ||
            this.isSPAN(node.parentNode) ||
            this.isA(node.parentNode))
      );
   }

   protected hasParentIdEqualTo(node: Node, elementId: string): boolean {
      return Boolean(node.parentElement) && Boolean(node.parentElement.id) && node.parentElement.id === elementId;
   }

   protected hasParentParagraph(node: Node): boolean {
      return Boolean(node.parentNode) && this.isP(node.parentNode);
   }

   protected getFirstNodeInline(node: Node, parameterElement?: Element): Node {
      const { isP, isBR, isDiv } = this;
      let currentNode = node;
      let previousNode: Node = node.previousSibling;

      if (Boolean(parameterElement)) {
         currentNode = parameterElement;
         previousNode = parameterElement.previousSibling;
      }
      // is the first
      if (!Boolean(previousNode) || isP(previousNode) || isBR(previousNode) || isDiv(previousNode)) {
         return currentNode;
      }

      if (this.hasStyledParent(currentNode)) {
         return this.getFirstNodeInline(currentNode.parentNode);
      }

      // still searching for the first
      return this.getFirstNodeInline(previousNode);
   }

   protected getLastNodeToEvaluate(node: Node, parameterElement?: Element): Node {
      let currentNode = node;
      let nextNode: Node = node.nextSibling;

      if (Boolean(parameterElement)) {
         currentNode = parameterElement;
         nextNode = parameterElement.nextSibling;
      }

      // is the last
      if (!Boolean(nextNode) || this.isP(nextNode) || this.isBR(nextNode) || this.isDiv(nextNode)) {
         return currentNode;
      }

      if (this.hasStyledParent(currentNode)) {
         return this.getLastNodeToEvaluate(currentNode.parentNode);
      }

      // still searching for the last
      return this.getLastNodeToEvaluate(nextNode);
   }

   protected hasParentNgModelAttribute(node: Node): boolean {
      return Boolean(node.parentNode) && this.hasNgModelAttribute(node.parentNode);
   }

   protected hasNgModelAttribute(node: Node): boolean {
      return this.hasAttribute(node, "ng-model");
   }

   protected hasAttribute(node: Node, attribute: string): boolean {
      if (!Boolean(node) || this.isText(node)) {
         return false;
      }
      const element = <Element>node;
      const attributeValue = element.getAttribute(attribute);
      return Boolean(attributeValue);
   }

   protected getNgModelValue(node: Node): string {
      const element = <Element>node;
      if (!Boolean(element.attributes["ng-model"])) {
         return "";
      }
      return element.attributes["ng-model"].value;
   }

   protected isSpanChildWithoutSibling(node: Node): boolean {
      return this.isSPAN(node.parentNode) && !Boolean(node.nextSibling) && !Boolean(node.previousSibling);
   }

   protected isUnderlineChildWithoutSibling(node: Node): boolean {
      return this.isU(node.parentNode) && !Boolean(node.nextSibling) && !Boolean(node.previousSibling);
   }

   protected getAllNextSiblingNodesFromNode(node: Node): ChildNode[] {
      let nodeList: ChildNode[] = [];
      let currentNode: Node = node;
      while (Boolean(currentNode.nextSibling)) {
         nodeList.push(currentNode.nextSibling);
         currentNode = currentNode.nextSibling;
      }
      return nodeList;
   }

   protected removeAllChildFromParent(parentNode: Node, children: Node[]): void {
      children.forEach((child) => {
         parentNode.removeChild(child);
      });
   }

   protected appendAllChildren(parentNode: ParentNode, children: Node[]): void {
      children.forEach((node) => {
         parentNode.append(node);
      });
   }

   protected getStartNodeTagAsString(node: Node, notApplyIf?: (node: Node) => boolean) {
      if (!Boolean(node) || this.isText(node)) {
         return "";
      }

      if (Boolean(notApplyIf) && notApplyIf(node)) {
         return "";
      }

      const startTagWithStyle = (<HTMLElement>node).outerHTML.split(">")[0];

      return `${startTagWithStyle}>`;
   }

   protected getEndNodeTagAsString(node: Node, notApplyIf?: (node: Node) => boolean) {
      if (!Boolean(node) || this.isText(node) || this.isBR(node)) {
         return "";
      }

      if (Boolean(notApplyIf) && notApplyIf(node)) {
         return "";
      }

      return `</${node.nodeName.toLowerCase()}>`;
   }

   protected getFirstTextOrParameterNodeIfExist(element: Node): Node {
      if (!Boolean(element)) {
         return element.parentNode;
      }

      if (this.isTemplateContext(element) || this.isText(element)) {
         return element;
      }

      return this.getFirstTextOrParameterNodeIfExist(element.firstChild);
   }

   protected getLastTextOrParameterNodeIfExist(element: Node, parentNode?: Node): Node | null {
      if (!Boolean(element)) {
         return parentNode ?? null;
      }

      if (this.isTemplateContext(element) || this.isText(element)) {
         return element;
      }

      return this.getLastTextOrParameterNodeIfExist(element.lastChild, element);
   }

   protected getRootNode(node: Node): Node {
      if (!Boolean(node)) {
         return null;
      }

      if (this.hasNgModelAttribute(node)) {
         return node;
      }

      return this.getRootNode(node.parentNode);
   }

   protected isStyledNodeWitEmptyText(node: Node): boolean {
      return (this.isB(node) || this.isU(node) || this.isSPAN(node)) && !Boolean(node.firstChild);
   }

   protected getLastTextNode(node: Node): Node | null {
      if (!Boolean(node)) {
         return null;
      }

      if (this.isText(node)) {
         return node;
      }

      if (Boolean(node.lastChild)) {
         return this.getLastTextNode(node.lastChild);
      }

      if (Boolean(node.previousSibling)) {
         return this.getLastTextNode(node.previousSibling);
      }

      if (Boolean(node.parentNode.previousSibling)) {
         return this.getLastTextNode(node.parentNode.previousSibling);
      }

      return this.getLastTextNode(node.parentNode);
   }

   protected getFirstTextNode(node: Node): Node | null {
      if (!Boolean(node)) {
         return null;
      }

      if (this.isText(node)) {
         return node;
      }

      if (Boolean(node.firstChild)) {
         return this.getFirstTextNode(node.firstChild);
      }

      if (Boolean(node.nextSibling)) {
         return this.getFirstTextNode(node.nextSibling);
      }

      if (Boolean(node.parentNode.nextSibling)) {
         return this.getFirstTextNode(node.parentNode.nextSibling);
      }

      return this.getFirstTextNode(node.parentNode);
   }

   protected hasIdEqualTo(node: Node, id: string): boolean {
      if (!Boolean(node) || this.isText(node)) {
         return false;
      }

      const elementNode = <Element>node;
      return elementNode.id === id;
   }

   protected hasSiblingNode(node: Node): boolean {
      return Boolean(node) && (Boolean(node.nextSibling) || Boolean(node.previousSibling));
   }

   protected isSelectionInlineText(range: Range): boolean {
      return (
         this.isText(range.startContainer) &&
         range.startContainer === range.endContainer &&
         range.startContainer === range.commonAncestorContainer
      );
   }
}
